import { Button } from "@progress/kendo-react-buttons";
import { DetailExpandDescriptor } from "@progress/kendo-react-data-tools";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDetailExpandChangeEvent,
} from "@progress/kendo-react-grid";
import {
  createDataTree,
  extendDataItem,
  mapTree,
  TreeList,
  TreeListCellProps,
  TreeListColumnProps,
  TreeListExpandChangeEvent,
} from "@progress/kendo-react-treelist";
import { useEffect, useState } from "react";
import { ActionDialog, StatusIcon } from ".";
import { getIconPath } from "../../../utilities/textUtilities";
import { CustomGridCell } from "../../grid";
import { Configuration, IConfiguration, IConfigurationDisplay } from "./Configuration";
import { ConfigAction, ConfigStatus } from "./ConfigurationEnums";
import { MarginNameCell } from "./MarginNameCell";

interface IConfigurationsGridByProductProps {
  data: Configuration[];
  childCompanyIds: number[];
  margins: any[];
  marginField: string;
  childMarginField?: string;
  onChange: () => void;
}

export const ConfigurationsGridByProduct = (props: IConfigurationsGridByProductProps) => {
  const { data, childCompanyIds, margins, marginField, onChange } = props;
  const [gridData, setGridData] = useState<any[]>([]);
  const [detailExpand, setDetailExpand] = useState<DetailExpandDescriptor>({});
  const [action, setAction] = useState("");
  const [actionItem, setActionItem] = useState<Configuration | null>(null);

  type DataType = {
    productId: number;
    commodity: string;
    instrument: string;
    expanded?: boolean;
    [key: string]: any;
  };

  useEffect(() => {
    // Group data by productId
    const grouped = (data ?? []).reduce(
      (acc, item) => {
        const key = item.productId;
        if (!acc[key]) acc[key] = { ...item, items: [], expanded: !!detailExpand[key] };
        acc[key].items.push(item);
        return acc;
      },
      {} as Record<
        number,
        { commodity: string; instrument: string; expanded: boolean; items: DataType[] }
      >,
    );

    const sorted = Object.values(grouped).sort((a, b) => {
      return a.commodity !== b.commodity
        ? a.commodity.localeCompare(b.commodity)
        : a.instrument.localeCompare(b.instrument);
    });

    if (JSON.stringify(sorted) !== JSON.stringify(gridData)) setGridData(sorted);
  }, [data]);

  const ProductCell = (props: GridCellProps) => {
    const { dataItem } = props;
    const { commodity, commodityType, instrument, productType, type } = dataItem;

    return (
      <CustomGridCell {...props}>
        <div style={{ display: "flex", gap: 10 }}>
          <div>
            <img
              alt={commodityType}
              src={getIconPath("commodity", commodityType)}
              title={commodityType}
              style={{ width: 16, height: 16, paddingRight: 3 }}
            />
            <span>{commodity}</span>
          </div>
          <div>
            <img
              alt={type}
              src={getIconPath("instrument", productType)}
              title={type}
              style={{ width: 16, height: 16, paddingRight: 3 }}
            />
            <span>{instrument}</span>
          </div>
        </div>
      </CustomGridCell>
    );
  };

  const handleDetailExpandChange = (event: GridDetailExpandChangeEvent) => {
    setDetailExpand(event.detailExpand);
  };

  const onAction = (action: string, item: Configuration) => {
    setAction(action);
    setActionItem(item);
  };

  const onActionComplete = () => {
    setAction("");
    onChange();
  };

  return (
    <>
      <Grid
        data={gridData}
        dataItemKey="productId"
        detail={(gridProps) => (
          <DetailComponent {...gridProps} {...props} action={action} onAction={onAction} />
        )}
        detailExpand={detailExpand}
        onDetailExpandChange={handleDetailExpandChange}
      >
        <GridColumn field="commodity" title="Product" cells={{ data: ProductCell }} />
      </Grid>

      {action && actionItem && (
        <ActionDialog
          action={action}
          data={{
            item: actionItem,
            margins: margins.filter((m) => !childCompanyIds.includes(m.companyId)),
            marginField,
          }}
          onClose={() => setAction("")}
          onSave={onActionComplete}
        />
      )}
    </>
  );
};

interface Node extends IConfiguration, IConfigurationDisplay {
  parentId?: string;
  expanded: boolean;
  children?: Node[];
  [key: string]: any;
}

const DetailComponent = (props: any) => {
  const { dataItem, childCompanyIds, childMarginField, action, onAction } = props;
  const subItemsField = "children";
  const expandField = "expanded";

  const [treeData, setTreeData] = useState<any[]>([]);

  useEffect(() => {
    const data: Node[] = dataItem?.items ?? [];
    if (!data || data.length === 0) return;

    const dataCopy = data.map((item) => ({ ...item, expanded: true })); // Expand nodes by default

    dataCopy.forEach((node) => {
      const key = node.parentConfigurationId || 0;
      const parent =
        key === 0 ? null : dataCopy.find((n) => n.configurationId === node.parentConfigurationId);
      node.parentId = parent?.id;
    });

    // Build tree
    const tree: Node[] = createDataTree(
      dataCopy,
      (n) => n.id,
      (n) => n.parentId,
      subItemsField,
    );

    setTreeData(tree);
  }, [dataItem]);

  const onExpandChange = (e: TreeListExpandChangeEvent) => {
    const { dataItem, value } = e;

    setTreeData(
      mapTree(treeData, subItemsField, (item) =>
        item.companyId === dataItem.companyId && item.productId === dataItem.productId
          ? extendDataItem(item, subItemsField, { expanded: !value })
          : item,
      ),
    );
  };

  const MarginCell = (props: TreeListCellProps) => {
    const { dataItem } = props;

    return (
      <td className="k-table-td">
        <MarginNameCell
          name={dataItem.margin}
          marginId={dataItem.marginId}
          marginCompanyId={dataItem.marginCompanyId}
        />
      </td>
    );
  };

  const ChildMarginCell = (props: TreeListCellProps) => {
    const { dataItem } = props;

    return (
      <td className="k-table-td">
        <MarginNameCell
          name={dataItem.childMargin}
          marginId={dataItem.childMarginId}
          marginCompanyId={dataItem.childMarginCompanyId}
        />
      </td>
    );
  };

  const StatusCell = (props: TreeListCellProps) => {
    const { dataItem } = props;
    const config = Configuration.create(dataItem, dataItem);
    const value = config.status;

    const status =
      value === ConfigStatus.Margins && dataItem.margin
        ? ConfigStatus.ChildMargins
        : (value as ConfigStatus);

    return (
      <td className="k-table-td" style={{ textAlign: "center" }}>
        <StatusIcon status={status} />
      </td>
    );
  };

  interface ActionsProps {
    cellProps: TreeListCellProps;
    action: string;
    onActionClick: (action: string, item: Configuration) => void;
  }

  const ActionsCell = ({ cellProps, action, onActionClick }: ActionsProps) => {
    const { dataItem } = cellProps;
    const config = Configuration.create(dataItem, dataItem);
    const disabled = action.length > 0;

    return (
      <td className="k-table-td">
        <div style={{ display: "flex" }}>
          {config.status !== ConfigStatus.Unassigned && (
            <Button
              disabled={disabled}
              icon="pencil"
              size="small"
              themeColor="primary"
              fillMode="outline"
              title="Assign Margin"
              onClick={() => onActionClick(ConfigAction.Edit, config)}
            />
          )}

          {config.configurationId > 0 && childCompanyIds.includes(config.companyId) && (
            <Button
              disabled={disabled}
              icon={`${config.isActive ? "cancel" : "check"}`}
              size="small"
              themeColor="primary"
              fillMode="outline"
              title={`${config.isActive ? "Suspend" : "Activate"}`}
              onClick={() =>
                onActionClick(
                  config.isActive ? ConfigAction.Suspend : ConfigAction.Activate,
                  config,
                )
              }
            />
          )}

          {config.status === ConfigStatus.Unassigned &&
            childCompanyIds.includes(config.companyId) && (
              <Button
                disabled={disabled}
                icon="plus"
                size="small"
                themeColor="primary"
                fillMode="outline"
                title="Assign"
                onClick={() => onActionClick(ConfigAction.Assign, config)}
              />
            )}
        </div>
      </td>
    );
  };

  const ActionsCellWrapper = (props: TreeListCellProps) => (
    <ActionsCell
      cellProps={props}
      action={action}
      onActionClick={(newAction: string) => onAction(newAction, props?.dataItem)}
    />
  );

  let columns: TreeListColumnProps[] = [
    { field: "company", title: "Company", expandable: true },
    { field: "margin", title: "Margin", cell: MarginCell },
    { field: "status", title: "Status", cell: StatusCell, width: 100 },
    { field: "configurationId", title: "Actions", cell: ActionsCellWrapper, width: 150 },
  ];

  if (childMarginField)
    columns.splice(2, 0, { field: "childMargin", title: "Broker Margin", cell: ChildMarginCell });

  return (
    <TreeList
      style={{ maxHeight: "510px", overflow: "auto" }}
      expandField={expandField}
      subItemsField={subItemsField}
      onExpandChange={onExpandChange}
      data={treeData}
      columns={columns}
      resizable={true}
    />
  );
};
